import { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarCheck, CreditCard, RotateCcw, Flower2, Clock, CalendarDays,
  Newspaper, Image, Users as UsersIcon, HandHeart, HelpCircle, FileText, BarChart3,
  Settings, ScrollText, LogOut, Menu, X, Landmark, Shield, UserCog, Megaphone, BookOpen,
  Sun, Moon,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const GROUPS = [
  { title: "Overview", items: [["/admin", "Dashboard", LayoutDashboard, null]] },
  { title: "Bookings & Finance", items: [
    ["/admin/bookings", "Bookings", CalendarCheck, "bookings"],
    ["/admin/payments", "Payment Verification", CreditCard, "payments"],
    ["/admin/reports", "Reports", BarChart3, "reports"],
  ]},
  { title: "Seva & Worship", items: [
    ["/admin/sevas", "Seva & Pooja", Flower2, "seva"],
    ["/admin/seva-categories", "Seva Categories", BookOpen, "seva"],
    ["/admin/timings", "Temple Timings", Clock, "worship"],
    ["/admin/deities", "Deities", Landmark, "deities"],
  ]},
  { title: "Content", items: [
    ["/admin/events", "Events & Festivals", CalendarDays, "events"],
    ["/admin/news", "News", Newspaper, "news"],
    ["/admin/announcements", "Announcements", Megaphone, "news"],
    ["/admin/journey", "Our Journey", ScrollText, "journey"],
    ["/admin/social", "Social Service", HandHeart, "social"],
    ["/admin/gallery", "Gallery", Image, "gallery"],
    ["/admin/faqs", "FAQ", HelpCircle, "faq"],
    ["/admin/legal", "Legal Pages", FileText, "content"],
  ]},
  { title: "Administration", items: [
    ["/admin/settings", "Settings", Settings, "content"],
    ["/admin/users", "Admin Users", UserCog, "*"],
    ["/admin/audit", "Audit Log", Shield, "*"],
  ]},
];

export default function AdminLayout({ children }) {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("smbm_admin_theme") || "dark");
  const navigate = useNavigate();

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("smbm_admin_theme", next);
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const visible = (perm) => perm === null || can(perm);

  return (
    <div className={`admin-theme ${theme === "light" ? "admin-light" : ""} min-h-screen bg-slate-900 text-slate-100 flex`}>
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-64 bg-slate-950 border-r border-slate-800 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
          <Landmark className="w-6 h-6 text-orange-500" />
          <div className="font-display font-bold text-sm leading-tight">SMBM Admin</div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 hide-scrollbar">
          {GROUPS.map((g) => {
            const items = g.items.filter(([, , , perm]) => visible(perm));
            if (!items.length) return null;
            return (
              <div key={g.title} className="mb-4">
                <div className="px-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{g.title}</div>
                {items.map(([to, label, Icon]) => (
                  <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-orange-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}
                    data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <Icon className="w-4 h-4 shrink-0" /> {label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
        <div className="border-t border-slate-800 p-3">
          <div className="px-2 mb-2">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-slate-400 capitalize">{user?.role?.replace(/_/g, " ")}</div>
          </div>
          <button onClick={doLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-slate-900/90 backdrop-blur border-b border-slate-800 flex items-center px-4 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="w-6 h-6" /></button>
          <Link to="/" className="text-sm text-slate-400 hover:text-white">← View Website</Link>
          <button onClick={toggleTheme} className="ml-auto p-2 rounded-lg hover:bg-slate-800 text-slate-300" title="Toggle light / dark" data-testid="admin-theme-toggle">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <div className="text-xs text-slate-500 hidden sm:block">{new Date().toLocaleDateString("en", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </header>
        <main className="p-4 md:p-6 max-w-[1400px] mx-auto">{children}</main>
      </div>
    </div>
  );
}
